import AwardsChatIcon from '@/components/icons/AwardsChatIcon';
import {
  Ban,
  ChevronLeft,
  ChevronRight,
  CircleX,
  Search,
  ShieldOff,
  icons as lucideIcons,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { banPresenceParticipant, fetchSessionPresence } from '@/features/live/liveThunks';
import type { PresenceParticipantDto } from '@/features/live/types';
import React, { useEffect, useState } from 'react';
import type { WorkspaceVisualIdentity } from '@/features/workspace/types';
import { toast } from 'react-toastify';

function iconNameToPascal(name: string): string {
  return name
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

export function LucideLazyIcon({
  name,
  ...rest
}: { name: string } & React.SVGProps<SVGSVGElement>) {
  if (!name || typeof name !== 'string') return null;
  const pascalName = iconNameToPascal(name);
  const IconComponent = (
    lucideIcons as Record<string, React.ComponentType<React.SVGProps<SVGSVGElement>>>
  )[pascalName];
  if (!IconComponent) return null;
  return <IconComponent {...rest} />;
}

type AttendeesListViewProps = {
  workspaceId: number;
  webinarId: number;
  sessionId: number;
  visualIdentity: WorkspaceVisualIdentity;
};

export default function AttendeesListView({
  workspaceId,
  webinarId,
  sessionId,
  visualIdentity,
}: AttendeesListViewProps) {
  const { t } = useTranslation('webinars');
  const dispatch = useAppDispatch();
  const { sessionPresence, sessionPresenceError } = useAppSelector((s: RootState) => s.live);

  const accentColor = visualIdentity.accentColor ?? '#2356CC';
  const contrastColor = visualIdentity.contrastColor ?? '#2356CC';
  const secondaryBg = visualIdentity.secondaryBackgroundColor ?? 'rgba(128, 128, 128, 0.12)';
  const isDarkTheme = visualIdentity.theme === 'DARK';
  const paginationIconColor = isDarkTheme ? '#FFFFFF' : contrastColor;
  const paginationMutedColor = isDarkTheme ? 'rgba(255, 255, 255, 0.72)' : 'rgba(0, 0, 0, 0.55)';
  // Texte principal lisible selon le thème (les tokens text-secondary/défaut restent sombres).
  const primaryText = isDarkTheme ? '#FFFFFF' : '#1F1F1F';
  const totalPages = sessionPresence?.totalPages ?? 0;
  type FilterValue =
    | 'all'
    | 'converted'
    | 'notConverted'
    | 'banned'
    | 'notBanned'
    | 'activity_asc'
    | 'activity_desc';

  const [filterValue, setFilterValue] = useState<FilterValue>('all');

  const onlyConverted =
    filterValue === 'converted' ? true : filterValue === 'notConverted' ? false : undefined;
  const onlyBanned =
    filterValue === 'banned' ? true : filterValue === 'notBanned' ? false : undefined;
  const orderBy = filterValue === 'activity_asc' ? 'activity_asc' : 'activity_desc';
  const [page, setPage] = useState(0);

  // Recherche par nom / email / identifiant, debouncée pour ne pas requêter à chaque frappe
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeoutId = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  useEffect(() => {
    const fetch = () => {
      void dispatch(
        fetchSessionPresence({
          workspaceId,
          webinarId,
          sessionId,
          onlyConverted,
          onlyBanned,
          orderBy,
          page,
          search: debouncedSearch || undefined,
        }),
      );
    };
    fetch();
    const intervalId = setInterval(fetch, 5000);
    return () => clearInterval(intervalId);
  }, [
    dispatch,
    workspaceId,
    webinarId,
    sessionId,
    onlyConverted,
    onlyBanned,
    orderBy,
    page,
    debouncedSearch,
  ]);

  const attendees = sessionPresence?.content ?? [];

  const handleBan = (participantId: number, isCurrentlyBanned: boolean) => {
    void dispatch(
      banPresenceParticipant({ workspaceId, webinarId, sessionId, subscriptionId: participantId }),
    ).then((result: { meta: { requestStatus: string } }) => {
      if (result.meta.requestStatus === 'fulfilled') {
        if (isCurrentlyBanned) {
          toast.success(t('adminLive.attendees.unbanned'));
        } else {
          toast.success(t('adminLive.attendees.banned'));
        }
        void dispatch(
          fetchSessionPresence({
            workspaceId,
            webinarId,
            sessionId,
            onlyConverted,
            onlyBanned,
            orderBy,
            page,
            search: debouncedSearch || undefined,
          }),
        );
      }
    });
  };

  return (
    <div
      className="flex flex-col h-full min-h-0 overflow-hidden"
      style={{
        backgroundColor:
          visualIdentity?.theme === 'LIGHT' ? 'rgba(31, 31, 31, 0.03)' : 'rgba(255, 255, 255, 0.1)',
      }}
    >
      <div className="pt-6 px-4 h-full min-h-0 flex flex-col">
        <div className="flex items-center justify-between gap-2 shrink-0">
          <p className="text-base" style={{ color: paginationMutedColor }}>
            {t('adminLive.attendees.title')}
          </p>
          <Select value={filterValue} onValueChange={(v) => setFilterValue(v as FilterValue)}>
            <SelectTrigger className="text-base px-2 h-fit py-1 gap-0 justify-center w-fit bg-medium max-w-[240px]">
              <SelectValue placeholder={t('adminLive.attendees.filterPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('adminLive.attendees.filterAll')}</SelectItem>
              <SelectItem value="converted">{t('adminLive.attendees.filterConverted')}</SelectItem>
              <SelectItem value="notConverted">
                {t('adminLive.attendees.filterNotConverted')}
              </SelectItem>
              <SelectItem value="banned">{t('adminLive.attendees.filterBanned')}</SelectItem>
              <SelectItem value="notBanned">{t('adminLive.attendees.filterNotBanned')}</SelectItem>
              <SelectItem value="activity_desc">
                {t('adminLive.attendees.filterActivityDesc')}
              </SelectItem>
              <SelectItem value="activity_asc">
                {t('adminLive.attendees.filterActivityAsc')}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="relative w-full mt-3 shrink-0">
          <Search
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary pointer-events-none"
          />
          <Input
            value={searchInput}
            onChange={(event) => {
              setSearchInput(event.target.value);
              setPage(0);
            }}
            placeholder={t('adminLive.attendees.searchPlaceholder')}
            aria-label={t('adminLive.attendees.searchPlaceholder')}
            className="pl-10 pr-10 h-10 bg-medium"
          />
          {searchInput && (
            <button
              type="button"
              aria-label={t('adminLive.attendees.clearSearch')}
              onClick={() => {
                setSearchInput('');
                setPage(0);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary cursor-pointer"
            >
              <CircleX size={18} />
            </button>
          )}
        </div>

        {sessionPresenceError && (
          <p className="text-sm text-red-500 mt-2">{sessionPresenceError}</p>
        )}

        <div className="flex flex-col gap-2 my-4 flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {attendees.map((attendee: PresenceParticipantDto, index) => (
            <div
              key={attendee.id ?? attendee.subscriptionId ?? index}
              className="flex gap-1 items-center shrink-0 justify-between"
            >
              <div className="flex gap-2 min-w-0">
                {attendee.converted && <AwardsChatIcon />}
                <div className="flex flex-col min-w-0">
                  <div className="flex items-center gap-1 flex-wrap">
                    {attendee.badgeIcon && (
                      <LucideLazyIcon
                        name={attendee.badgeIcon}
                        width={14}
                        height={14}
                        color={attendee.badgeColor ?? '#2C6CFF'}
                      />
                    )}
                    <span
                      className="text-base"
                      style={{ color: attendee.badgeColor ?? primaryText }}
                    >
                      {attendee.fullName ?? attendee.displayName ?? attendee.subscriptionId ?? '-'}{' '}
                      {attendee.badgeLabel && (
                        <span className="text-tertiary"> - {attendee.badgeLabel}</span>
                      )}
                    </span>
                    {attendee.banned && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-600 font-medium shrink-0">
                        {t('adminLive.attendees.bannedBadge')}
                      </span>
                    )}
                  </div>
                  {attendee.email && (
                    <p className="text-xs" style={{ color: paginationMutedColor }}>
                      {attendee.email}
                    </p>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() =>
                  attendee.id != null && handleBan(attendee.id, attendee.banned === true)
                }
                className="shrink-0 p-1 rounded transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-red-500 hover:text-red-600"
                aria-label={
                  attendee.banned
                    ? t('adminLive.attendees.unbanLabel')
                    : t('adminLive.attendees.banLabel')
                }
              >
                {attendee.banned ? <ShieldOff className="size-5" /> : <Ban className="size-5" />}
              </button>
            </div>
          ))}
          {attendees.length === 0 && (
            <p
              className="text-base text-center mt-4 shrink-0"
              style={{ color: paginationMutedColor }}
            >
              {debouncedSearch
                ? t('adminLive.attendees.emptySearch')
                : t('adminLive.attendees.empty')}
            </p>
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setPage(Math.max(0, page - 1))}
                disabled={page === 0 || totalPages <= 0}
                className="p-2 rounded-lg cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors hover:brightness-110"
                style={{
                  backgroundColor: secondaryBg,
                  color: paginationIconColor,
                }}
              >
                <ChevronLeft size={16} color={paginationIconColor} aria-hidden />
              </button>

              <div className="flex items-center gap-1 mx-2">
                <span className="text-sm font-medium tabular-nums" style={{ color: accentColor }}>
                  {page + 1}
                </span>
                <span className="text-sm tabular-nums" style={{ color: paginationMutedColor }}>
                  /
                </span>
                <span className="text-sm tabular-nums" style={{ color: paginationMutedColor }}>
                  {totalPages}
                </span>
              </div>

              <button
                type="button"
                onClick={() => {
                  if (totalPages <= 0) return;
                  setPage(Math.min(totalPages - 1, page + 1));
                }}
                disabled={totalPages <= 0 || page >= totalPages - 1}
                className="p-2 rounded-lg cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed transition-colors hover:brightness-110"
                style={{
                  backgroundColor: secondaryBg,
                  color: paginationIconColor,
                }}
              >
                <ChevronRight size={16} color={paginationIconColor} aria-hidden />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
